import { usePostHog } from 'posthog-js/react';
import { logger } from '@common';

declare global {
  interface Window {
    DD_RUM?: {
      addError: (error: Error, context?: Record<string, unknown>) => void;
      addAction: (name: string, context?: Record<string, unknown>) => void;
    };
  }
}

interface AnalyticsProperties {
  [key: string]: unknown;
}

interface AnalyticsError extends Error {
  message: string;
  stack?: string;
}

/**
 * Custom hook for dual analytics tracking - DataDog and PostHog
 * Provides unified interface for tracking events and errors across both platforms
 */
export const useAnalytics = () => {
  const posthog = usePostHog();

  /**
   * Track any event (success, failure, or general action)
   * @param eventName - Name of the event
   * @param properties - Event properties/context
   */
  const trackEvent = (eventName: string, properties?: AnalyticsProperties) => {
    // Debug logging
    logger.debug('Analytics trackEvent:', {
      eventName,
      properties,
      dataDogAvailable: !!window.DD_RUM,
      postHogAvailable: !!posthog,
    });

    // DataDog tracking
    if (window.DD_RUM) {
      window.DD_RUM.addAction(eventName, properties);
      logger.debug('DataDog event sent successfully');
    } else {
      logger.debug('DataDog not available - event would be sent to DD_RUM.addAction');
    }

    // PostHog tracking
    if (posthog) {
      posthog.capture(eventName, properties);
      logger.debug('PostHog event sent successfully');
    } else {
      logger.debug('PostHog not available - event would be sent to posthog.capture');
    }
  };

  /**
   * Track an error/exception
   * @param error - Error object
   * @param eventName - Name for the PostHog event
   * @param context - Additional context for both platforms
   */
  const trackError = (error: AnalyticsError, eventName: string, context?: AnalyticsProperties) => {
    // Debug logging
    logger.debug('Analytics trackError:', {
      eventName,
      errorMessage: error.message,
      errorStack: error.stack,
      context,
      dataDogAvailable: !!window.DD_RUM,
      postHogAvailable: !!posthog,
    });

    // DataDog error tracking (uses dedicated addError method)
    if (window.DD_RUM) {
      window.DD_RUM.addError(error, context);
      logger.debug('DataDog error sent successfully');
    } else {
      logger.debug('DataDog not available - error would be sent to DD_RUM.addError');
    }

    // PostHog error tracking (uses capture with error details)
    if (posthog) {
      posthog.capture(eventName, {
        errorMessage: error.message,
        errorStack: error.stack,
        ...context,
      });
      logger.debug('PostHog error sent successfully');
    } else {
      logger.debug('PostHog not available - error would be sent to posthog.capture');
    }
  };

  return {
    trackEvent,
    trackError,
  };
};
